/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import BSSRValidationListTable from '../../../components/customTables/validation/bssrListTable';
import SearchHeader from '../../../components/searchHeader';
import ValidationSideNav from '../../../components/sideNav/ValidationSideNav';

import { RootState } from '../../../redux/store';
import { useGetSupportSubjectsToRoundQuery } from '../../../redux/apis/Users.api';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps } from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ValidationContainer } from '../styles/Validation.styles';

export interface IBSSRValidationListProps {
  authorizedUser?: AuthUserProps;
  selectedDeptsFromStore: string[];
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const BSSRValidationList: FC<IBSSRValidationListProps> = ({
  authorizedUser,
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const { validationFilter = 'all' } = useParams();

  const {
    data: bssrValidationSubjectsToRoundData,
    // isError: isGetSupportSubjectsToRoundError,
    // isFetching: isGetSupportSubjectsToRoundFetching,
    isLoading: isGetSupportSubjectsToRoundLoading
  } = useGetSupportSubjectsToRoundQuery(
    {
      facilityId: authorizedUser?.facilityId ?? '',
      hcaid: authorizedUser?.hcaid ?? '',
      type: 'bssrValidation'
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !(authorizedUser?.hcaid && authorizedUser?.facilityId)
    }
  );

  const filteredValidationRounds = useMemo(() => {
    return (
      bssrValidationSubjectsToRoundData?.filter(employee => {
        const employeeDept =
          (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
          'Unknown';
        return selectedDeptsFromStore.some(
          selectedDept => selectedDept === employeeDept
        );
      }) ?? []
    );
  }, [bssrValidationSubjectsToRoundData, selectedDeptsFromStore]);

  const validationCounts = useMemo(() => {
    const validationSubjects = filteredValidationRounds.filter(
      employee => !employee.adHoc
    );
    const validationAdHocs = filteredValidationRounds.filter(
      employee => employee.adHoc
    );

    const completedCount =
      validationSubjects.filter(
        employee => employee?.complianceStatus === 'Completed'
      ).length + validationAdHocs.length;

    const toDoCount = validationSubjects.filter(
      employee =>
        employee?.complianceStatus === 'Todo' ||
        employee?.complianceStatus === 'Overdue' ||
        !employee.complianceStatus
    ).length;

    const expertCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_BSSR_E')
    ).length;

    const competentCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_BSSR_C')
    ).length;

    const developingCount = validationSubjects.filter(employee =>
      employee.lastRound?.tag.includes('Validation_BSSR_D')
    ).length;

    return {
      total: filteredValidationRounds.length,
      completedCount,
      toDoCount,
      expertCount,
      competentCount,
      developingCount
    };
  }, [filteredValidationRounds]);

  return (
    <ValidationContainer data-testid="bssr-validation-view">
      <ValidationSideNav
        type="bssr"
        total={validationCounts.total}
        completedCount={validationCounts.completedCount}
        toDoCount={validationCounts.toDoCount}
        expertCount={validationCounts.expertCount}
        competentCount={validationCounts.competentCount}
        developingCount={validationCounts.developingCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <BSSRValidationListTable
          isLoading={isGetSupportSubjectsToRoundLoading}
          searchTerm={searchTerm}
          validationFilter={validationFilter}
          validationRounds={filteredValidationRounds}
        />
      </div>
    </ValidationContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(BSSRValidationList);
